// src/components/MapView.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker, Popup, Polyline, useMap } from 'react-leaflet';
import ClusterGroup from 'react-leaflet-cluster';
import { debounce, groupBy } from 'lodash';
import 'leaflet/dist/leaflet.css';
import L, { setupLeafletIcons } from '../utils/leaflet';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

import api from '../api';
import { CITIES, DEFAULT_CITY } from '../config/cities';

// FIX: Leaflet default icon configuration
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});
setupLeafletIcons();


const baseURL = process.env.REACT_APP_API_BASE_URL || 'https://tretacre-backend-production.up.railway.app/api';

const createClusterCustomIcon = (cluster) => {
  return L.divIcon({
    html: `<div style="background-color: purple; border-radius: 50%; color: white; text-align: center; line-height: 30px; width: 30px; height: 30px;">${cluster.getChildCount()}</div>`,
    className: 'custom-cluster-icon',
    iconSize: [30, 30],
  });
};

const calculateOffset = (index, count) => {
  const radius = 0.0001;
  const angle = (2 * Math.PI * index) / count;
  return [radius * Math.cos(angle), radius * Math.sin(angle)];
};

// ✅ NEW: Helper component — reliably accesses the Leaflet map instance
function MapFocusHandler({ focusId, properties }) {
  const map = useMap();

  useEffect(() => {
    if (!focusId || properties.length === 0) return;

    const target = properties.find(p => p._id === focusId);
    if (!target || !target.latitude || !target.longitude) {
      console.warn('[MapFocusHandler] Focused property not in current filtered list:', focusId);
      return;
    }

    console.log('[MapFocusHandler] Centering on:', target.location, [target.latitude, target.longitude]);
    map.flyTo([target.latitude, target.longitude], 16, { duration: 1.2 });
  }, [focusId, properties, map]);

  return null;
}

function MapView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const focusId = searchParams.get('focus');  
  const [focusProperty, setFocusProperty] = useState(null);  
 
  const [selectedCity, setSelectedCity] = useState(() => {
    return localStorage.getItem('selectedCity') || DEFAULT_CITY;
  });

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tempSearch, setTempSearch] = useState('');
  const [filters, setFilters] = useState({
    lease: true,
    buy: true,
    tretaExclusive: false,
    subscriber: false,
    maxLeasePrice: 30,
    maxBuyPrice: 100,
    search: '',
    propertyCategory: 'all',
  });
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  const currentCity = CITIES[selectedCity] || CITIES.pune;

    // ✅ NEW: When focus ID is present, fetch the property and align filters
  useEffect(() => {
    if (!focusId) {
      setFocusProperty(null);
      return;
    }

    const fetchFocusedProperty = async () => {
      try {
        console.log('[MapView] Fetching focused property:', focusId);
        const response = await api.get(`/properties/${focusId}`);
        const property = response.data;

        console.log('[MapView] Focused property loaded:', property.location, '| City:', property.city);
        setFocusProperty(property);

        // ✅ Auto-align city to match the focused property
        if (property.city && property.city !== selectedCity) {
          console.log(`[MapView] Auto-switching city from ${selectedCity} to ${property.city}`);
          setSelectedCity(property.city);
        }

        // ✅ Auto-align category to match the focused property
        const warehouseTypes = ['Warehouse Rent', 'Warehouse Sale', 'Industrial Land', 'Factory', 'Godown', 'Cold Storage', 'Logistics Park'];
        const isWarehouse = warehouseTypes.includes(property.property_type);
        if (isWarehouse && selectedCategory !== 'warehouse') {
          console.log('[MapView] Auto-switching category to warehouse');
          setSelectedCategory('warehouse');
        } else if (!isWarehouse && selectedCategory !== 'office' && property.property_type?.includes('Office')) {
          console.log('[MapView] Auto-switching category to office');
          setSelectedCategory('office');
        }

        // ✅ Reset price filters so the property isn't excluded
        setFilters((prev) => ({
          ...prev,
          maxLeasePrice: 1000,  // Very high — includes everything
          maxBuyPrice: 1000,
        }));
      } catch (err) {
        console.error('[MapView] Failed to fetch focused property:', err);
      }
    };

    fetchFocusedProperty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId]);

  useEffect(() => {
    localStorage.setItem('selectedCity', selectedCity);
  }, [selectedCity]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

    // ✅ NEW: Merge focused property into the display list if not already there
  const allProperties = useMemo(() => {
    if (!focusProperty) return properties;
    const exists = properties.some((p) => p._id === focusProperty._id);
    if (exists) return properties;
    // Ensure focus property has coordinates
    if (!focusProperty.latitude || !focusProperty.longitude) return properties;
    return [...properties, focusProperty];
  }, [properties, focusProperty]);

  const groupedProperties = useMemo(() => {
    const grouped = groupBy(allProperties, prop => `${prop.latitude},${prop.longitude}`);
    const result = Object.entries(grouped).map(([key, props]) => ({
      lat: parseFloat(key.split(',')[0]),
      lng: parseFloat(key.split(',')[1]),
      properties: props,
    }));
    return result;
  }, [allProperties]);

  const toggleGroup = (key) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const fetchData = useMemo(
    () =>
      debounce(async (filters, city, category, setLoading, setError, setProperties) => {
        setLoading(true);
        setError(null);
        const searchParams = new URLSearchParams();

        if (filters.search && filters.search.trim()) {
          searchParams.set('search', filters.search.trim());
        }

        if (city === 'both') {
          searchParams.set('city', 'mumbai,pune');
        } else {
          searchParams.set('city', city);
        }

        if (category === 'warehouse') {
          searchParams.set('propertyCategory', 'warehouse');
        } else if (category === 'office') {
          searchParams.set('propertyCategory', 'office');
        }

        if (filters.tretaExclusive) {
          searchParams.set('tretaExclusive', 'true');
        }

        searchParams.set('maxLeasePrice', filters.maxLeasePrice);
        searchParams.set('maxBuyPrice', filters.maxBuyPrice);

        if (category === 'all') {
          if (filters.lease) {
            searchParams.append('type', 'Office Lease');
            searchParams.append('type', 'Warehouse Rent');
            searchParams.append('type', 'Cold Storage');
          }
          if (filters.buy) {
            searchParams.append('type', 'Office Sale');
            searchParams.append('type', 'Warehouse Sale');
            searchParams.append('type', 'Industrial Land');
            searchParams.append('type', 'Factory');
            searchParams.append('type', 'Godown');
          }
        }

        try {
          console.log(`Fetching properties with params: ${searchParams.toString()}`);
          const response = await api.get(`/properties?${searchParams.toString()}`);
          const data = Array.isArray(response.data) ? response.data : [];
          const filteredData = data.filter(prop => prop.latitude && prop.longitude);
          setProperties(filteredData);
          
          if (filteredData.length === 0) {
            setError(`No properties found in ${CITIES[city]?.name || city}. Try adjusting filters.`);
          }
        } catch (error) {
          console.error('Error fetching properties:', error.response || error.message);
          setError(`Failed to fetch properties: ${error.message}`);
        } finally {
          setLoading(false);
        }
      }, 300),
    []
  );

  const debouncedFetchData = useCallback(
    (filters, city, category) => {
      fetchData(filters, city, category, setLoading, setError, setProperties);
    },
    [fetchData]
  ); 

  // ✅ THE MISSING EFFECT — triggers the fetch on filter/city/category change
  useEffect(() => {
    debouncedFetchData(filters, selectedCity, selectedCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedCity, selectedCategory]);

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      setFilters({ ...filters, search: tempSearch });
    }
  };

  const handleCityChange = (cityId) => {
    setSelectedCity(cityId);
    setExpandedGroups(new Set());
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setFilters({ ...filters, propertyCategory: category });
  };

  const isWarehouseProperty = (property) => {
    const warehouseTypes = ['Warehouse Rent', 'Warehouse Sale', 'Industrial Land', 'Factory', 'Godown', 'Cold Storage', 'Logistics Park'];
    return warehouseTypes.includes(property.property_type);
  };

  const getPropertyColor = (property) => {
    if (isWarehouseProperty(property)) {
      return 'orange';
    }
    return property.property_type?.toLowerCase().includes('lease') ? 'blue' : 'red';
  };

  const getPropertyIcon = (property) => {
    if (isWarehouseProperty(property)) {
      return '🏭';
    }
    return property.property_type?.toLowerCase().includes('lease') ? '📋' : '💰';
  };

  return (
    <div className="relative">
      {focusId && (
        <div className="fixed top-0 left-0 right-0 bg-yellow-400 text-yellow-900 px-3 py-1.5 text-xs z-30 md:ml-14 flex items-center justify-between">
          <span>⭐ Showing highlighted property on map</span>
          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/property/${focusId}`)}
              className="bg-yellow-600 hover:bg-yellow-700 text-white px-2 py-0.5 rounded"
            >
              ← Back to Details
            </button>
            <button
              onClick={() => {
                searchParams.delete('focus');
                setSearchParams(searchParams);
              }}
              className="bg-yellow-600 hover:bg-yellow-700 text-white px-2 py-0.5 rounded"
            >
              Clear
            </button>
          </div>
        </div>
      )}
      {/* City and Category Selector */}
      <div className="fixed top-0 left-0 right-0 bg-gradient-to-r from-teal-600 to-teal-700 text-white p-2 shadow-md z-20 md:ml-14">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-bold">Treta: Commercial Real Estate</h2>
          <div className="flex flex-wrap gap-2">
            {Object.values(CITIES).map(city => (
              <button
                key={city.id}
                onClick={() => handleCityChange(city.id)}
                className={`px-3 py-1 rounded text-xs font-medium transition-all ${
                  selectedCity === city.id
                    ? 'bg-white text-teal-600'
                    : 'bg-teal-500 hover:bg-teal-400 text-white'
                }`}
              >
                {city.name}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleCategoryChange('all')}
              className={`px-3 py-1 rounded text-xs font-medium transition-all ${
                selectedCategory === 'all'
                  ? 'bg-white text-teal-600'
                  : 'bg-teal-500 hover:bg-teal-400 text-white'
              }`}
            >
              All
            </button>
            <button
              onClick={() => handleCategoryChange('office')}
              className={`px-3 py-1 rounded text-xs font-medium transition-all ${
                selectedCategory === 'office'
                  ? 'bg-white text-teal-600'
                  : 'bg-teal-500 hover:bg-teal-400 text-white'
              }`}
            >
              🏢 Office
            </button>
            <button
              onClick={() => handleCategoryChange('warehouse')}
              className={`px-3 py-1 rounded text-xs font-medium transition-all ${
                selectedCategory === 'warehouse'
                  ? 'bg-white text-teal-600'
                  : 'bg-teal-500 hover:bg-teal-400 text-white'
              }`}
            >
              🏭 Warehouse
            </button>
          </div>
        </div>
      </div>

      {/* Top Bar */}
      <div className="fixed top-14 left-0 right-0 bg-white p-1 shadow-sm z-10 md:ml-14">
        <div className="flex items-center justify-between max-w-4xl mx-auto flex-col sm:flex-row gap-1">
          <input
            type="text"
            placeholder="Search by location or building name..."
            className="border p-1 rounded-sm w-full sm:flex-1 text-xs"
            value={tempSearch}
            onChange={e => setTempSearch(e.target.value)}
            onKeyPress={handleSearchKeyPress}
          />
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600">
              {allProperties.length} properties
              {selectedCategory === 'warehouse' && ' 🏭'}
            </span>
            <button
              className="bg-teal-600 text-white p-1 rounded-sm text-xs hover:bg-teal-700"
              onClick={() => window.location.href = `${baseURL}/export?city=${selectedCity}`}
            >
              CSV Export
            </button>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className={`${focusId ? 'pt-40' : 'pt-32'} sm:pt-28 md:ml-14`}>
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap gap-1 mb-0.5">
            <label className="flex items-center text-[12px]">
              <input
                type="checkbox"
                checked={filters.lease}
                onChange={e => setFilters({ ...filters, lease: e.target.checked })}
                className="mr-0.5 w-2 h-2"
                disabled={selectedCategory !== 'all'}
              />
              Lease
            </label>
            <label className="flex items-center text-[12px]">
              <input
                type="checkbox"
                checked={filters.buy}
                onChange={e => setFilters({ ...filters, buy: e.target.checked })}
                className="mr-0.5 w-2 h-2"
                disabled={selectedCategory !== 'all'}
              />
              Buy
            </label>
            <label className="flex items-center text-[12px]">
              <input
                type="checkbox"
                checked={filters.tretaExclusive}
                onChange={e => setFilters({ ...filters, tretaExclusive: e.target.checked })}
                className="mr-0.5 w-2 h-2"
              />
              Treta Excl.
            </label>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="pt-32 sm:pt-28 md:ml-14">
        {error && <p className="text-center text-red-500 text-sm p-2">{error}</p>}
        {loading && <p className="text-center text-gray-500 text-sm p-2">Loading properties...</p>}
        {!loading && !error && allProperties.length === 0 && (
          <p className="text-center text-red-500 text-sm p-2">No properties found. Try adjusting filters.</p>
        )}
        <MapContainer 
          center={currentCity.center} 
          zoom={currentCity.zoom} 
          style={{ 
            height: isMobile ? '50vh' : 'calc(100vh - 200px)',
            width: '100%' 
          }}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
          <MapFocusHandler focusId={focusId} properties={allProperties} />
          <ClusterGroup
            maxClusterRadius={50}
            iconCreateFunction={createClusterCustomIcon}
          >
            {groupedProperties.map(group => {
              const key = `${group.lat},${group.lng}`;
              const isExpanded = expandedGroups.has(key);
              const isMultiple = group.properties.length > 1;

              if (isMultiple && !isExpanded) {
                return (
                  <CircleMarker
                    key={key}
                    center={[group.lat, group.lng]}
                    radius={12}
                    fillOpacity={0.3}
                    color="purple"
                    fillColor="purple"
                    eventHandlers={{ click: () => toggleGroup(key) }}
                  >
                    <Popup>
                      <div>
                        <h3 className="font-bold">{group.properties.length} Properties</h3>
                        <ul className="text-xs">
                          {group.properties.slice(0, 5).map(prop => (
                            <li key={prop.propertyID}>
                              {prop.location} ({prop.property_type})
                            </li>
                          ))}
                          {group.properties.length > 5 && (
                            <li className="text-gray-500">... and {group.properties.length - 5} more</li>
                          )}
                        </ul>
                        <button 
                          onClick={() => toggleGroup(key)}
                          className="mt-2 bg-teal-500 text-white px-2 py-1 rounded text-xs"
                        >
                          Expand All
                        </button>
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              }

              return group.properties.map((prop, index) => {
                const offset = isMultiple ? calculateOffset(index, group.properties.length) : [0, 0];
                const markerPos = [group.lat + offset[0], group.lng + offset[1]];
                const isWarehouse = isWarehouseProperty(prop);
                const color = getPropertyColor(prop);
                const icon = getPropertyIcon(prop);

                const isFocused = focusId === prop._id;

                return (
                  <React.Fragment key={prop.propertyID}>
                    <CircleMarker
                      center={markerPos}
                      radius={isFocused ? 14 : (isWarehouse ? 10 : 8)}
                      color={isFocused ? 'gold' : color}
                      fillColor={isFocused ? 'gold' : color}
                      fillOpacity={isFocused ? 0.9 : 0.6}
                      weight={isFocused ? 3 : 1}
                    >
                      <Popup maxWidth={300}>
                        <div className="text-sm">
                          {isFocused && (
                            <div className="bg-yellow-100 border border-yellow-300 text-yellow-800 text-xs px-2 py-1 rounded mb-2">
                              ⭐ Highlighted Property
                            </div>
                          )}
                          <div className="text-2xl">{icon}</div>
                          <h3 className="font-bold text-base">{prop.location || 'N/A'}</h3>
                          <div className="mt-1 space-y-1">
                            <p><span className="font-semibold">Type:</span> {prop.property_type}</p>
                            <p><span className="font-semibold">Price:</span> {prop.price || 'Price on Request'}</p>
                            <p><span className="font-semibold">Area:</span> {prop.carpet_area || 'N/A'}</p>
                            <p><span className="font-semibold">Sub Locality:</span> {prop.sub_locality || 'N/A'}</p>
                            
                            {isWarehouse && (
                              <div className="mt-2 p-2 bg-orange-50 rounded border border-orange-200">
                                <p className="font-semibold text-orange-700">🏭 Warehouse Details</p>
                                {prop.ceiling_height && prop.ceiling_height !== 'N/A' && (
                                  <p><span className="font-semibold">Ceiling:</span> {prop.ceiling_height}</p>
                                )}
                                {prop.number_of_docks > 0 && (
                                  <p><span className="font-semibold">Docks:</span> {prop.number_of_docks}</p>
                                )}
                                {prop.power_supply && prop.power_supply !== 'N/A' && (
                                  <p><span className="font-semibold">Power:</span> {prop.power_supply}</p>
                                )}
                              </div>
                            )}

                            {prop.highlights && prop.highlights !== 'N/A' && (
                              <p><span className="font-semibold">Highlights:</span> {prop.highlights}</p>
                            )}
                            {prop.is_treta_exclusive && (
                              <p className="text-teal-600 font-semibold">⭐ Treta Exclusive</p>
                            )}
                          </div>
                          <button
                            onClick={() => navigate(`/property/${prop._id}`)}
                            className="mt-2 w-full bg-teal-600 hover:bg-teal-700 text-white px-2 py-1 rounded text-xs"
                          >
                            👁️ View Full Details
                          </button>
                        </div>
                      </Popup>
                    </CircleMarker>
                    {isMultiple && (
                      <Polyline
                        positions={[markerPos, [group.lat, group.lng]]}
                        color="gray"
                        weight={1}
                        opacity={0.4}
                      />
                    )}
                  </React.Fragment>
                );
              });
            })}
          </ClusterGroup>
        </MapContainer>
      </div>
    </div>
  );
}

export default MapView;
